/*
 * A dropdown select component for selecting external entities.
 * Possibility for autofill based on the context where the form is filled (project). 
 */

import React from 'react';

import {FormContext} from '../form_context.jsx';

import {
  Mode,
  castDataType,
  DataType,
} from '../common.jsx';
import Setting from './setting.jsx';
import Helpers from './helpers.jsx';
import DropdownSelectionWithAsyncOptions from './dropdown_selection_with_async_options.jsx';

import { fetchJson, cachedFetchJson } from '../../../helpers/fetch.js';

import {
  integration_list_entities_path,
  integration_get_external_entity_dependency_map_path
} from '~/routes';

export default class EntityLookupSelect extends DropdownSelectionWithAsyncOptions {
  static contextType = FormContext;

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
        deletable: false,
        sortable: false,
        reportKey: '',
        options: []
    });
  }

  static get TYPE() {
    return 'adalia_me_entity_lookup_select';
  }

  externalEntityLinking = () => {
    if (this.props.content.endpoint) {
      let options = { unique: true } 
      return ActionJS.Integration.form_editor_load_external_entity_linking(this.props.content.endpoint, options)
    }
    return null;
  }

  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = '') {
    var namesOrIds = [...super.resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope)]
    if (content.dependentOnField && content.dependentOnField != ''){
      namesOrIds.push(content.dependentOnField);
    }
    return namesOrIds
  }

  renderSettings() {
    return <>
      <Setting.ExternalEntityLinker 
        name="lookupComponentSelectField" 
        label={I18n.t('select_by', {scope: 'fe'})} 
        value={this.props.content.lookupComponentSelectField} 
        onChange={this.updateSettings} 
        description={I18n.t('field_external_entity_accessor_description', {scope: 'fe'})} 
        externalEntityLinkingCallback={this.externalEntityLinking}
        endpoint={this.props.content.endpoint}
        componentId={this.props.content.id}
      />

      <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} description={this.props.content.lockOptionValues ? I18n.t('locked_field_label_description', {scope: 'fe'}) : ''} />
      <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          componentType={this.constructor.TYPE}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />
      <Setting.Checkbox
        name="selectByContext" 
        label={I18n.t('select_by_context', {scope: 'fe'})} 
        value={this.props.content.selectByContext} 
        onChange={this.updateSettings} 
        description={I18n.t('field_external_entity_select_by_context_description', {scope: 'fe'})}
        disabled={!this.props.content.endpoint}
      />
      {this.props.content.selectByContext && <Setting.Checkbox
        name="lockSelectByContext" 
        label={I18n.t('lock_select_by_context', {scope: 'fe'})} 
        value={this.props.content.lockSelectByContext} 
        onChange={this.updateSettings} 
        description={I18n.t('field_external_entity_lock_select_by_context_description', {scope: 'fe'})}
        disabled={!this.props.content.endpoint}
      />}

      <Setting.Dropdown 
        name="dependentOnField"
        label={I18n.t('dependent_on_field', {scope: 'fe'})}
        value={this.props.content.dependentOnField}
        onChange={this.updateSettings} 
        description={I18n.t('field_external_entity_dependent_on_field_description', {scope: 'fe'})}
        disabled={!this.props.content.endpoint || !this.state.dependencyConfigured || this.props.content.lockSelectByContext}
        options={this.dependencyComponentOptions()}
      />
    </>
  }

  beforeUpdateSettings(content) {
    super.beforeUpdateSettings(content);
    content.selectByContext = castDataType(content.selectByContext, DataType.BOOLEAN);
    content.lockSelectByContext = castDataType(content.lockSelectByContext, DataType.BOOLEAN);
  }


  dependencyComponentOptions() {
    let options = [["", I18n.t('field_default_file_selector_please_select', {scope: 'fe'})]];

    if (this.props.findComponentsCallback) {
      [
        ...this.props.findComponentsCallback({
          className: "EntityLookupSelect",
        }),
      ].forEach((c) => {
        if (c.id != this.props.content.id) {
          let label = Helpers.getText(c.translations, c.label, this.props.language);
          options.push([c.name, label]);
        }
      });
    }
    return options;
  }


  // Loads async options if proper props given. Uses cached responses
  maybeLoadDetails = () => {
    if (this.props.endpoint && (this.props.content?.lookupComponentSelectField) ) {

      let options = {
        object_type: this.props.endpoint, 
        accessor: this.props.content.lookupComponentSelectField
      }

      if (this.props.content.selectByContext) {
        options['project_id'] = this.context?.projectId
      }

      let answers = this.getAnswer()

      fetchJson(integration_list_entities_path(options), {useCache: true})
        .then(res => {
          const preSelectedIndex = res.findIndex(r => { return r[2] });
          let formattedOptions = res
            .filter(r => !this.props.content.lockSelectByContext || !this.context?.projectId || r[2]) // If locked, only allow the linked ones
            .filter(r => options.object_type != 'NDA_contact' || r[3].active || answers.includes(r[0]))// Filter for NDA_contact based on status
            .map((r) => {
              if(options.object_type == 'NDA_contact'){
                return {value: r[0], label: r[1], disabled: !r[3].active}//Options disabled for NDA_contact in case of selected and then inactived
              }else{
                return {value: r[0], label: r[1]}
              }
            }) // Map value & label
            // Update answer to the linked one if configured. Only in FILL mode 
            if (this.props.content.selectByContext && this.props.mode == Mode.FILL && preSelectedIndex != -1) {
              this.props.updateAnswerCallback(this.fullFieldName(), this.getAnswer() || res[preSelectedIndex][0]);
            }
            this.setState({allOptions: formattedOptions, options: formattedOptions, loading: false}, this.initSelect2);
        })
    }
    if (this.props.content.endpoint) {
      cachedFetchJson(integration_get_external_entity_dependency_map_path()).then(response => {
        const dependencyConfigured = Object.values(response).some(d => Object.keys(d).includes(this.props.content.endpoint))
        this.setState({dependencyMap: response, dependencyConfigured: dependencyConfigured})
      })
    }
  }

  componentDidMount() {
    super.componentDidMount();
    this.maybeLoadDetails();
  }

  filterOptionsByDependency = () => {
    let opts = [...this.state.allOptions];
    return opts.filter(o => (this.state.dependencyMap[this.props.answers[this.props.content.dependentOnField]][this.props.content.endpoint] || []).includes(o.value))
  }

  componentDidUpdate(prevProps, _) {
    if (prevProps.endpoint != this.props.endpoint || prevProps.content.accessor != this.props.content.accessor) {
        this.maybeLoadDetails();
    }
    // The answer of dependency changed, filter options
    if (this.props.content.dependentOnField && this.props.answers && prevProps.answers[this.props.content.dependentOnField] != this.props.answers[this.props.content.dependentOnField]) {
      this.setState({options: this.filterOptionsByDependency()}, () => {
        const answer = (this.getAnswer() || [])[0];
        if (answer && !this.state.options.find(o => o.value == answer)) {
          this.props.updateAnswerCallback(this.fullFieldName(), undefined);
        }
        this.initSelect2();
      });
    }
  }


  initSelect2 = () => {
    if (this.props.mode == Mode.FILL) {
      $(`[data-id=${this.props.content.id}].fill .select2-container`).select2('destroy');
      $(`[data-id=${this.props.content.id}].fill .select2-with-hierarchy`).select2({
        disabled: (this.props.mode == Mode.FILL && this.isReadOnlyAnswer()) || this.props.mode != Mode.FILL
      })
        .on('change', () => this.updateAnswer())
        .on('select2:opening', this.selectClicked);
    }
  }
}
